import { useEffect, useState, useRef, Fragment } from "react";
import "@/App.css";
import {
  nav, home, principle, projectApproach, technology, value, safety, resources, company, investors, contact, footer, market, productSeries,
  irContact, hydralytica, about, leadership, careers, globalPresence, legal,
  listProducts, getProduct, getSeries, listIndustries, getIndustry, listApplications, getApplication, getBenefitPage,
} from "@/content";
import {
  ArrowRight, ArrowUpRight, ChevronLeft, Menu, X, Ship, Factory, Mountain, Droplets,
  Wrench, ChevronRight, Building2, LineChart, Globe2, Zap, Activity, Gauge, Leaf, Recycle,
  Plus, Minus, DollarSign, BadgeCheck, Search, ClipboardCheck, Settings, TrendingUp,
  FileText, Newspaper, Presentation, ShieldCheck, MapPin, Briefcase, Mail, Phone, ExternalLink,
} from "lucide-react";

const LOGO = {
  colour: "/assets/logos/dynacert-logo-ohne-claim.png",
  whiteClaim: "/assets/logos/dynacert-logo-white.png",
  white: "/assets/logos/dynacert-logo-white-ohne-claim.png",
  hydralytica: "/assets/logos/hydralytica-logo.png",
};

const navigate = (to) => {
  if (to !== window.location.pathname) {
    window.history.pushState({}, "", to);
    window.dispatchEvent(new Event("navigate"));
  }
};
const usePath = () => {
  const [p, setP] = useState(window.location.pathname || "/");
  useEffect(() => {
    const on = () => { setP(window.location.pathname || "/"); window.scrollTo(0, 0); };
    window.addEventListener("popstate", on);
    window.addEventListener("navigate", on);
    return () => { window.removeEventListener("popstate", on); window.removeEventListener("navigate", on); };
  }, []);
  return p;
};

/* ---------------- Per-route SEO (smallest robust head manager for this SPA) ---------------- */
const SITE = process.env.REACT_APP_SITE_URL || "https://dynacert.com";
const OG_IMG = SITE + "/assets/hero/coastal-clean-plate.jpg";
const SEO = {
  "/": { t: "dynaCERT | HydraGEN™ Hydrogen-on-Demand for Diesel Equipment", d: "dynaCERT HydraGEN™ delivers hydrogen-on-demand retrofits for heavy-duty diesel engines — supporting cleaner, more efficient combustion with HydraLytica™ operational visibility." },
  "/technology": { t: "HydraGEN™ Technology | dynaCERT", d: "How HydraGEN™ hydrogen-on-demand works: an electrolysis-based retrofit that introduces hydrogen and oxygen into the air intake of existing diesel engines." },
  "/products": { t: "HydraGEN™ Products | dynaCERT", d: "The HydraGEN™ product family — hydrogen-on-demand units for trucks, generators and heavy-duty off-road and industrial diesel engines." },
  "/products/hg1r": { t: "HydraGEN™ HG1R | dynaCERT", d: "HydraGEN™ HG1R hydrogen-on-demand unit for smaller diesel engines and on-highway applications." },
  "/products/hg2r": { t: "HydraGEN™ HG2R | dynaCERT", d: "HydraGEN™ HG2R hydrogen-on-demand unit for medium and heavy-duty diesel engines." },
  "/products/hg4c": { t: "HydraGEN™ HG4C | dynaCERT", d: "HydraGEN™ HG4C hydrogen-on-demand unit for large diesel engines, gensets and industrial applications." },
  "/products/hg6c": { t: "HydraGEN™ HG6C | dynaCERT", d: "HydraGEN™ HG6C hydrogen-on-demand unit for the largest diesel engines and industrial power applications." },
  "/applications": { t: "Applications | dynaCERT", d: "HydraGEN™ applications across trucking, mining, ports and harbours, power generation and industrial diesel operations." },
  "/industries/ports-harbours": { t: "Ports & Harbours | dynaCERT", d: "HydraGEN™ hydrogen-on-demand for diesel equipment used in ports and harbours operations." },
  "/industries/power-generation": { t: "Power Generation | dynaCERT", d: "HydraGEN™ hydrogen-on-demand for diesel gensets and power-generation applications." },
  "/industries/mining": { t: "Mining | dynaCERT", d: "HydraGEN™ hydrogen-on-demand for heavy-duty diesel equipment in mining operations." },
  "/industries/oil-gas": { t: "Oil & Gas | dynaCERT", d: "HydraGEN™ hydrogen-on-demand for diesel equipment in oil and gas operations." },
  "/why-hydragen": { t: "Why HydraGEN™ | dynaCERT", d: "Why operators choose HydraGEN™ hydrogen-on-demand retrofit technology for existing heavy-duty diesel fleets and equipment." },
  "/hydralytica": { t: "HydraLytica™ Telematics | dynaCERT", d: "HydraLytica™ provides connected operational visibility for HydraGEN™-equipped diesel engines." },
  "/value/operational-efficiency": { t: "Operational Efficiency | dynaCERT", d: "How HydraGEN™ supports operational efficiency for heavy-duty diesel fleets and equipment." },
  "/value/fuel-efficiency": { t: "Fuel Efficiency | dynaCERT", d: "How HydraGEN™ hydrogen-on-demand supports more efficient diesel combustion." },
  "/value/lower-emissions": { t: "Lower Emissions | dynaCERT", d: "How HydraGEN™ hydrogen-on-demand supports cleaner diesel combustion and emissions-reduction goals." },
  "/value/carbon-value": { t: "Carbon & Reporting | dynaCERT", d: "How HydraGEN™ and HydraLytica™ data support carbon and operational reporting for diesel operations." },
  "/resources": { t: "Resources | dynaCERT", d: "HydraGEN™ technical resources, third-party reports and documentation for diesel retrofit applications." },
  "/company": { t: "Company | dynaCERT", d: "dynaCERT designs, manufactures and distributes HydraGEN™ hydrogen-on-demand technology for diesel engines worldwide." },
  "/company/about": { t: "About dynaCERT | dynaCERT", d: "About dynaCERT — a clean-technology company delivering HydraGEN™ hydrogen-on-demand retrofits for diesel engines." },
  "/company/leadership": { t: "Leadership | dynaCERT", d: "The dynaCERT leadership team behind HydraGEN™ hydrogen-on-demand technology." },
  "/company/global-presence": { t: "Global Presence | dynaCERT", d: "dynaCERT's global distribution and presence for HydraGEN™ hydrogen-on-demand technology." },
  "/company/careers": { t: "Careers | dynaCERT", d: "Careers at dynaCERT — join the team advancing HydraGEN™ hydrogen-on-demand technology." },
  "/investors": { t: "Investor Relations | dynaCERT", d: "dynaCERT investor relations — stock listings, identifiers and links to authoritative regulatory and disclosure materials." },
  "/contact": { t: "Contact | dynaCERT", d: "Contact dynaCERT to discuss HydraGEN™ hydrogen-on-demand technology for your diesel equipment or application." },
};
function setMeta(attr, key, val) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", val);
}
function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
function applySeo(pathname) {
  const key = (pathname || "/").replace(/\/+$/, "") || "/";
  const known = SEO[key];
  const m = known || { t: "Page not found | dynaCERT", d: "The page you requested could not be found on dynacert.com." };
  const canonical = key === "/" ? SITE + "/" : SITE + key;
  document.title = m.t;
  setMeta("name", "description", m.d);
  setMeta("name", "robots", known ? "index, follow" : "noindex, follow");
  setCanonical(canonical);
  setMeta("property", "og:title", m.t);
  setMeta("property", "og:description", m.d);
  setMeta("property", "og:url", canonical);
  setMeta("property", "og:image", OG_IMG);
}
const IND_ICON = { "ports-harbours": Ship, "power-generation": Factory, mining: Mountain, "oil-gas": Droplets };
const IC = (n) => `/assets/icons/${n}.png`;
const BEN_ICON = [IC("B17_combustion_efficiency"), IC("A03_emission_pressure"), IC("A09_operational_efficiency"), IC("B08_retrofit"), IC("H07_carbon_credits")];
const HERO_LAYERS = [
  { slug: "hg1r", name: "HydraGEN™ HG1R", img: "/assets/hero/hg1r.png", href: "/products/hg1r" },
  { slug: "hg2", name: "HydraGEN™ HG2R", img: "/assets/hero/hg2.png", href: "/products/hg2r" },
  { slug: "hg4c", name: "HydraGEN™ HG4C", img: "/assets/hero/hg4c.png", href: "/products/hg4c" },
  { slug: "hg6c", name: "HydraGEN™ HG6C", img: "/assets/hero/hg6c.png", href: "/products/hg6c" },
];
const HL_ICON = { H01_operational_dashboard: "H01_operational_dashboard", H02_fleet_visibility: "H02_fleet_visibility", H04_reporting: "H04_reporting", H05_analytics: "H05_analytics", H06_carbon_readiness: "H06_carbon_readiness", H09_remote_diagnostics: "H09_remote_diagnostics" };

const usePrefersReducedMotion = () => {
  const [r, setR] = useState(false);
  useEffect(() => { const m = window.matchMedia("(prefers-reduced-motion: reduce)"); const on = () => setR(m.matches); on(); m.addEventListener?.("change", on); return () => m.removeEventListener?.("change", on); }, []);
  return r;
};
/* Reveal-on-scroll: adds .in when the element enters the viewport (respects reduced-motion via CSS). */
const useInView = (opts = { threshold: 0.2 }) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, opts);
    io.observe(el); return () => io.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [ref, seen];
};
/* Layered hero product cascade: white unit opens & resets; one unit takes focus at a time; fade/depth/settle. */
function HeroCascade() {
  const items = HERO_LAYERS; const n = items.length;
  const [active, setActive] = useState(0);
  const reduced = usePrefersReducedMotion();
  useEffect(() => { if (reduced) return; const t = setInterval(() => setActive((a) => (a + 1) % n), 4200); return () => clearInterval(t); }, [reduced, n]);
  return (
    <div className="hero-stage" data-testid="hero-stage">
      <div className="hero-stage-inner">
        {items.map((p, idx) => { const slot = (idx - active + n) % n; return (
          <img key={p.slug} src={p.img} alt={slot === 0 ? `${p.name} unit` : ""} aria-hidden={slot !== 0} className={`hero-prod slot-${slot}`} data-testid={`hero-prod-${p.slug}`} />); })}
      </div>
      <a className="hero-prod-hit" href={items[active].href} aria-label={`Explore ${items[active].name}`} data-testid="hero-prod-link" />
      <div className="hero-controls">
        <a className="hero-prod-name" href={items[active].href} data-testid="hero-prod-name">{items[active].name}</a>
        <div className="hero-nav-row">
          <button className="hero-nav" aria-label="Previous unit" onClick={() => setActive((a) => (a - 1 + n) % n)} data-testid="hero-prev"><ChevronLeft size={20} /></button>
          <button className="hero-nav" aria-label="Next unit" onClick={() => setActive((a) => (a + 1) % n)} data-testid="hero-next"><ChevronRight size={20} /></button>
        </div>
        <div className="hero-dots" role="tablist" aria-label="HydraGEN units">
          {items.map((p, idx) => <button key={p.slug} role="tab" aria-selected={active === idx} aria-label={p.name} className={active === idx ? "on" : ""} onClick={() => setActive(idx)} data-testid={`hero-dot-${idx}`} />)}
        </div>
      </div>
    </div>
  );
}

const Btn = ({ href, variant = "primary", dark, children, testid }) => (
  <a data-testid={testid} href={href} className={`btn btn-${variant} ${dark ? "on-dark" : ""}`}>
    {children}{variant === "primary" && <ArrowRight size={16} />}
  </a>
);
const LogoSlot = ({ testid }) => (
  <a href="/" aria-label="dynaCERT — home" data-testid={testid} className="logo-slot"><img src={LOGO.colour} alt="dynaCERT" className="logo-img" /></a>
);
const MediaSlot = ({ ar = "ar-16-9", light, src, alt, label, testid, className = "" }) => (
  <div className={`media-slot ${ar} ${light ? "light" : ""} ${src ? "has-img" : ""} ${className}`} data-testid={testid} role="img" aria-label={alt || label}>
    {src ? <img src={src} alt={alt || ""} loading="lazy" className="ms-img" /> : <div className="ms-inner"><span className="ms-tag">dynaCERT imagery</span><span className="ms-label">{label}</span></div>}
  </div>
);

/* ---------------- Header ---------------- */
function Header() {
  const [open, setOpen] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const hdrRef = useRef(null);
  const industries = listIndustries();
  const apps = listApplications();
  const mega = {
    technology: (<div className="grid g3">
      <div><div className="mega-col-title">How it works</div>
        <a className="mega-item" href="/technology">Technology Overview</a><a className="mega-item" href="/technology">The HydraGEN™ Principle</a><a className="mega-item" href="/technology">Hydrogen-on-Demand</a></div>
      <div><div className="mega-col-title">Trust & integration</div>
        <a className="mega-item" href="/technology">Safety & Integrity</a><a className="mega-item" href="/technology">Monitoring · HydraLytica™</a><a className="mega-item" href="/technology">Thermodynamics</a></div>
      <div className="card"><div className="tag">See it work</div><p className="small" style={{ margin: "10px 0 14px" }}>An interactive walk-through of the HydraGEN™ Principle.</p><Btn href="/technology" variant="ghost" testid="mega-tech-cta">Explore the Principle</Btn></div>
    </div>),
    products: (<div className="grid g3">
      <div><div className="mega-col-title">The unit family</div>{listProducts().map((p) => <a key={p.slug} className="mega-item" href={`/products/${p.slug}`}>{p.name}</a>)}</div>
      <div><div className="mega-col-title">Series</div>{productSeries.map((s) => <div key={s.id} className="small" style={{ padding: ".5rem 0" }}>{s.series} · {s.band}</div>)}</div>
      <div className="card"><div className="tag">Project-based</div><p className="small" style={{ margin: "10px 0 14px" }}>The right unit is confirmed by an application assessment.</p><Btn href="/products" variant="ghost" testid="mega-prod-cta">Find the right solution</Btn></div>
    </div>),
    applications: (<div className="grid g3">
      <div><div className="mega-col-title">Browse by industry</div>{industries.map((i) => <a key={i.slug} className="mega-item" href={`/industries/${i.slug}`}>{i.name}</a>)}</div>
      <div><div className="mega-col-title">Browse by equipment</div>{apps.map((a) => <a key={a.slug} className="mega-item" href={`/applications/${a.slug}`}>{a.name}</a>)}</div>
      <a className="mega-feature" href={`/industries/${industries[0].slug}`}><img src={industries[0].image} alt={industries[0].alt} /><span className="mega-feature-cap"><span className="tag">Featured</span><b>{industries[0].name}</b></span></a>
    </div>),
    why: (<div className="grid g3">
      <div><div className="mega-col-title">Customer value</div>{value.narratives.map((p) => <a key={p.name} className="mega-item" href="/why-hydragen">{p.name}</a>)}</div>
      <div><div className="mega-col-title">Approach</div><a className="mega-item" href="/why-hydragen">Engineered Project Approach</a><a className="mega-item" href="/value/carbon-value">Carbon Value Potential</a><a className="mega-item" href="/hydralytica">HydraLytica™ monitoring</a></div>
      <div className="card"><div className="tag">Case by case</div><p className="small" style={{ margin: "10px 0 14px" }}>We turn technology into value — evaluated for your operation.</p><Btn href="/contact" variant="ghost" testid="mega-why-cta">Discuss Your Application</Btn></div>
    </div>),
    company: (<div className="grid g3">
      <div><div className="mega-col-title">Company</div><a className="mega-item" href="/company">About dynaCERT</a><a className="mega-item" href="/company/leadership">Leadership</a><a className="mega-item" href="/company/global-presence">Global Presence</a><a className="mega-item" href="/company/careers">Careers</a></div>
      <div><div className="mega-col-title">Corporate</div><a className="mega-item" href="/investors">Investor Relations</a><a className="mega-item" href="/investors/news">News Releases</a><a className="mega-item" href="/investors/governance">Corporate Governance</a></div>
      <div className="card"><div className="tag">People trust people</div><p className="small" style={{ margin: "10px 0 14px" }}>An engineering-led clean-technology company behind serious industrial projects.</p><Btn href="/company" variant="ghost" testid="mega-company-cta">About dynaCERT</Btn></div>
    </div>),
  };
  const withMega = ["technology", "products", "applications", "why", "company"];
  return (
    <header className="hdr" ref={hdrRef} onMouseLeave={() => setOpen(null)}
      onBlur={(e) => { if (hdrRef.current && !hdrRef.current.contains(e.relatedTarget)) setOpen(null); }}
      onKeyDown={(e) => { if (e.key === "Escape") setOpen(null); }}>
      <div className="hdr-util"><div className="container" style={{ display: "flex", justifyContent: "flex-end" }}>
        {nav.utility.map((u) => <a key={u.label} href={u.href} data-testid={`util-${u.label.toLowerCase()}`}>{u.label}</a>)}
      </div></div>
      <div className="container" style={{ display: "flex", alignItems: "center", height: 78, gap: 24 }}>
        <LogoSlot testid="brand-logo" />
        <nav className="desktop-nav" aria-label="Primary" style={{ display: "flex", gap: 28, marginLeft: 8 }}>
          {nav.primary.map((n) => { const has = withMega.includes(n.id); return (
            <div key={n.id} onMouseEnter={() => setOpen(has ? n.id : null)} style={{ display: "flex", alignItems: "center" }}>
              <a className="navlink" href={n.href} data-testid={`nav-${n.id}`} aria-haspopup={has || undefined} aria-expanded={has ? open === n.id : undefined}
                onFocus={() => setOpen(has ? n.id : null)} onKeyDown={(e) => { if (has && e.key === "ArrowDown") { e.preventDefault(); setOpen(n.id); } }}>{n.label}</a>
            </div>); })}
        </nav>
        <div style={{ marginLeft: "auto", display: "flex", gap: 14, alignItems: "center" }}>
          <a href={nav.cta.href} className="btn btn-primary cta-desktop" data-testid="header-cta">{nav.cta.label}<ArrowRight size={16} /></a>
          <button className="menu-btn" onClick={() => setDrawer(true)} data-testid="menu-open" aria-label="Open menu"><Menu /></button>
        </div>
      </div>
      {open && withMega.includes(open) && (<div className="mega" data-testid={`mega-${open}`} onMouseLeave={() => setOpen(null)}>
        <div className="container" style={{ padding: "34px 32px" }}>{mega[open]}</div></div>)}
      {drawer && (<div className="drawer" data-testid="mobile-drawer">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 26 }}>
          <img src={LOGO.white} alt="dynaCERT" style={{ height: 30 }} /><button onClick={() => setDrawer(false)} data-testid="menu-close" aria-label="Close menu"><X color="#fff" /></button></div>
        {nav.primary.map((n) => <a key={n.id} href={n.href} onClick={() => setDrawer(false)} data-testid={`drawer-nav-${n.id}`} style={{ display: "block", padding: "15px 0", borderBottom: "1px solid #22384c", fontFamily: "Barlow", fontWeight: 700, fontSize: 21 }}>{n.label}</a>)}
        <div style={{ marginTop: 22 }}><Btn href="/contact" testid="drawer-cta">Discuss Your Application</Btn></div>
        <div style={{ marginTop: 26, display: "flex", gap: 18 }}>{nav.utility.map((u) => <a key={u.label} href={u.href} onClick={() => setDrawer(false)} className="small" style={{ color: "#8593a0" }}>{u.label}</a>)}</div>
      </div>)}
    </header>
  );
}

const Footer = () => (
  <footer className="section dark" style={{ paddingBottom: "2.5rem", paddingTop: "clamp(3.5rem,6vw,5rem)" }}><div className="container">
    <div className="footer-grid">
      <div><img src={LOGO.whiteClaim} alt="dynaCERT — Carbon Emission Reduction Technology" style={{ height: 46 }} data-testid="footer-logo" />
        <p className="small" style={{ color: "#8593a0", marginTop: 16, maxWidth: 240 }}>Hydrogen-on-demand retrofit technology for industrial diesel applications.</p></div>
      {footer.columns.map((c) => <div key={c.title}><div className="mega-col-title" style={{ color: "#7f8a94" }}>{c.title}</div>
        {c.links.map((l) => <a key={l.label} href={l.href} className="small" style={{ display: "block", padding: "6px 0", color: "#c3ccd3" }}>{l.label}</a>)}</div>)}
    </div>
    <div className="divide-t" style={{ marginTop: 44, paddingTop: 22, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
      <span className="small" style={{ color: "#7f8a94" }}>© {new Date().getFullYear()} dynaCERT Inc. · Toronto, Canada · dynaCERT GmbH, Munich, Germany</span>
      <span style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>{footer.legal.map((l) => <a key={l.label} href={l.href} className="small" style={{ color: "#7f8a94" }} data-testid={`footer-legal-${l.label.replace(/\s+/g, "-").toLowerCase()}`}>{l.label}</a>)}</span>
    </div>
  </div></footer>
);

const PageHead = ({ crumb, eyebrow, title, lead, tight }) => (
  <section className="section paper2" style={{ paddingBottom: "3rem" }}><div className="container">
    <div className="crumb rise">{crumb}</div>
    <div className="eyebrow rise" style={{ marginTop: 18, display: "block" }}>{eyebrow}</div>
    <h1 className="h1 rise rise-2" style={{ marginTop: 14, maxWidth: "20ch" }}>{title}</h1>
    {lead && <p className="lead rise rise-3" style={{ marginTop: tight ? 10 : 22 }}>{lead}</p>}
  </div></section>
);
const CtaBand = () => (
  <section className="section graphite"><div className="container" style={{ textAlign: "center" }}>
    <div className="eyebrow on-dark">Start a conversation</div>
    <h2 className="h2" style={{ margin: "16px auto 12px", maxWidth: "20ch" }}>Could HydraGEN™ work for your diesel assets?</h2>
    <p className="lead" style={{ margin: "0 auto 30px" }}>We evaluate every application case by case — no obligation.</p>
    <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
      <Btn href="/contact" testid="band-cta">Discuss Your Application</Btn><Btn href="/technology" variant="ghost" dark testid="band-tech">How it works</Btn></div>
  </div></section>
);

/* ============ Signature: HydraGEN Principle — layered, faithful, responsive ============ */
const PRN_ART = [["1.png"], ["2.png"], ["3.png"], ["4_1.png", "4_2.png"]];
function Principle() {
  const s = principle.steps;
  const [i, setI] = useState(0);
  const [touched, setTouched] = useState(false);
  const tabs = useRef([]);
  const step = s[i];
  const sel = (n) => { setI(n); setTouched(true); };
  const onKey = (e) => {
    let n = i;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") n = (i + 1) % s.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") n = (i - 1 + s.length) % s.length;
    else if (e.key === "Home") n = 0; else if (e.key === "End") n = s.length - 1; else return;
    e.preventDefault(); sel(n); tabs.current[n]?.focus();
  };
  return (
    <section className="section dark principle" id="principle" data-testid="principle">
      <div className="container">
        <div className="eyebrow on-dark">{principle.eyebrow}</div>
        <h2 className="h2" style={{ margin: "12px 0 10px", maxWidth: "22ch" }}>{principle.title}</h2>
        <p className="lead" style={{ marginBottom: 30 }}>{principle.intro}</p>
      </div>
      {/* The authoritative dynaCERT Scratch-Art master is shown at rest; interaction gently lifts the
          matching region from the SAME master image (pixel-aligned) — never replacing the artwork. */}
      <div className="prm-wrap">
        <div className={`prm-stage ${touched ? "touched" : ""}`} data-testid="scratch-master">
          <img className="prm-master" src="/assets/principle/master.png" alt="HydraGEN™ Principle: on-demand electrolysis adds hydrogen and oxygen for enhanced combustion, reducing fuel and emissions" />
          {s.map((st, idx) => <img key={`ov${idx}`} src="/assets/principle/master.png" alt="" aria-hidden="true" className={`prm-slice s${idx} ${i === idx ? "on" : ""}`} />)}
          {s.map((st, idx) => <button key={`hot${idx}`} className={`prm-hot h${idx}`} tabIndex={-1} aria-hidden="true" onMouseEnter={() => sel(idx)} onClick={() => sel(idx)} />)}
        </div>
        <div className="prm-tabs" role="tablist" aria-label="HydraGEN Principle stages" onKeyDown={onKey}>
          {s.map((st, idx) => (
            <button key={st.id} ref={(el) => (tabs.current[idx] = el)} role="tab" id={`prn-tab-${idx}`} aria-selected={i === idx}
              aria-controls="prn-panel" tabIndex={i === idx ? 0 : -1} className={`prm-tab ${i === idx ? "on" : ""}`}
              aria-label={`Stage ${st.n}: ${st.label}`} data-testid={`principle-step-${idx}`}
              onClick={() => sel(idx)} onMouseEnter={() => sel(idx)} onFocus={() => sel(idx)}>
              <span className="prm-tab-n">{st.n}</span><span className="prm-tab-l">{st.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="container">
        <div className="prn-panel card dark" id="prn-panel" role="tabpanel" aria-labelledby={`prn-tab-${i}`} data-testid="principle-panel">
          <div className="prn-panel-grid">
            <div className="prn-panel-head">
              <div className="prn-step-n">Stage 0{step.n}</div>
              <h3 className="h3" style={{ fontSize: "1.5rem", margin: "6px 0 0" }}>{step.label}</h3>
            </div>
            <div>
              <div className="prn-what"><span className="prn-kicker">What happens</span><p>{step.what}</p></div>
              <div className="prn-why"><span className="prn-kicker green">Why it matters</span><p>{step.why}</p></div>
              {step.thermo.length > 0 && <div className="prn-chips"><span className="prn-chips-t"><Zap size={13} /> Thermodynamic effect</span>{step.thermo.map((t) => <span key={t} className="chip">{t}</span>)}</div>}
              {step.value.length > 0 && <div className="prn-chips"><span className="prn-chips-t green"><Gauge size={13} /> Customer value</span>{step.value.map((t) => <span key={t} className="chip green">{t}</span>)}</div>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ Second experience: Engineered Project Approach ============ */
const APR_ICON = [IC("D01_application_assessment"), IC("D08_technical_analysis"), IC("D02_engineering"), IC("D03_installation"), IC("D05_operation"), IC("D06_scaling")];
function ProjectApproach() {
  const st = projectApproach.stages;
  const [i, setI] = useState(0);
  const tabs = useRef([]);
  const stage = st[i];
  const onKey = (e) => {
    let n = i;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") n = (i + 1) % st.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") n = (i - 1 + st.length) % st.length;
    else if (e.key === "Home") n = 0; else if (e.key === "End") n = st.length - 1; else return;
    e.preventDefault(); setI(n); tabs.current[n]?.focus();
  };
  return (
    <section className="section approach" data-testid="approach"><div className="container">
      <div className="eyebrow">{projectApproach.eyebrow}</div>
      <h2 className="h2" style={{ margin: "12px 0 10px", maxWidth: "24ch" }}>{projectApproach.title}</h2>
      <p className="lead" style={{ marginBottom: 30 }}>{projectApproach.intro}</p>
      <div className="apr-track" style={{ "--p": `${(i / (st.length - 1)) * 100}%` }}>
        <div className="apr-rail" role="tablist" aria-label="Project approach stages" onKeyDown={onKey}>
          {st.map((s, idx) => { const ic = APR_ICON[idx]; return (
            <button key={s.key} ref={(el) => (tabs.current[idx] = el)} role="tab" id={`apr-tab-${idx}`} aria-selected={i === idx}
              aria-controls="apr-panel" tabIndex={i === idx ? 0 : -1} className={`apr-node ${i === idx ? "on" : ""} ${i > idx ? "past" : ""}`}
              data-testid={`approach-step-${idx}`} onClick={() => setI(idx)} onMouseEnter={() => setI(idx)} onFocus={() => setI(idx)}>
              <span className="apr-dot">{s.n}</span><img src={ic} alt="" className="gicon apr-ic" /><span className="apr-name">{s.name}</span></button>); })}
        </div>
      </div>
      <div className="apr-panel card" id="apr-panel" role="tabpanel" aria-labelledby={`apr-tab-${i}`} data-testid="approach-panel">
        <div className="grid g2" style={{ gap: 36, alignItems: "start" }}>
          <div><div className="prn-step-n" style={{ color: "var(--brand)" }}>Step 0{stage.n}</div>
            <h3 className="h3" style={{ fontSize: "1.6rem", margin: "6px 0 0" }}>{stage.name}</h3>
            <div className="apr-cta"><Btn href={projectApproach.cta.href} testid="approach-cta">{projectApproach.cta.label}</Btn></div></div>
          <div>
            <div className="prn-what"><span className="prn-kicker" style={{ color: "var(--brand)" }}>What happens</span><p style={{ color: "#3f4f5c" }}>{stage.what}</p></div>
            <div className="prn-what"><span className="prn-kicker" style={{ color: "var(--brand)" }}>What you can expect</span><p style={{ color: "#3f4f5c" }}>{stage.expect}</p></div>
            <div className="prn-why"><span className="prn-kicker green">Why it matters</span><p style={{ color: "#3f4f5c" }}>{stage.why}</p></div></div>
        </div>
      </div>
    </div></section>
  );
}

/* ---------------- Value deep-dive (accordion) ---------------- */
function ValueDeepDive({ items, dark }) {
  const [open, setOpen] = useState(0);
  return (
    <div className={`vdd ${dark ? "on-dark" : ""}`}>
      {items.map((n, idx) => (
        <div key={n.name} className={`vdd-item ${open === idx ? "open" : ""}`} data-testid={`value-deep-${idx}`}>
          <button className="vdd-head" aria-expanded={open === idx} onClick={() => setOpen(open === idx ? -1 : idx)} data-testid={`value-toggle-${idx}`}>
            <span className="vdd-n">0{idx + 1}</span>
            <span className="vdd-name">{n.name}</span>
            <span className="vdd-ic">{open === idx ? <Minus size={20} /> : <Plus size={20} />}</span>
          </button>
          <div className="vdd-body"><div className="vdd-body-in">
            <p className="vdd-lead">{n.text}</p>
            {n.deeper && <p className="vdd-deeper">{n.deeper}</p>}
            <div className="valnar-tags">{n.points.map((pt) => <span key={pt} className="tag">{pt}</span>)}</div>
            <div style={{ marginTop: 18, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}><Btn href="/contact" variant="ghost" dark={dark} testid={`value-cta-${idx}`}>Discuss Your Application</Btn>{n.href && <a href={n.href} className="vdd-more" data-testid={`value-more-${idx}`}>Learn more<ArrowUpRight size={14} /></a>}</div>
          </div></div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Product Family full-width stage ---------------- */
function ProductFamilyStage({ products }) {
  const [active, setActive] = useState(-1);
  return (
    <div className="pfam" data-testid="product-family-stage">
      {products.map((p, idx) => { const sr = getSeries(p.series); return (
        <div key={p.slug} className={`pfam-card ${active === idx ? "on" : ""}`} onMouseEnter={() => setActive(idx)} onMouseLeave={() => setActive(-1)} data-testid={`pfam-${p.slug}`}>
          <span className="pfam-series">{sr.series}</span>
          <div className="pfam-stage"><img src={p.image} alt={p.alt} loading="lazy" /></div>
          <div className="pfam-name">{p.name}</div>
          <a href={`/products/${p.slug}`} className="pfam-panel" onFocus={() => setActive(idx)} onBlur={() => setActive(-1)} data-testid={`pfam-link-${p.slug}`}>
            <span className="pfam-panel-model">{p.name}</span>
            <ul>{(p.facts || []).map((f) => <li key={f}>{f}</li>)}</ul>
            <span className="pfam-explore">Explore product <ArrowUpRight size={15} /></span>
          </a>
        </div>); })}
    </div>
  );
}

/* ---------------- Home ---------------- */
function Home() {
  const h = home;
  const inds = listIndustries();
  const prods = listProducts();
  const [carbonRef, carbonSeen] = useInView();
  return (
    <>
      {/* 01 LAYERED HERO — environment + HydraGEN unit reveal + copy/CTA layers */}
      <section className="hero-full layered" data-testid="home-hero">
        <img src={h.hero.image} alt={h.hero.alt} className="hero-bg" />
        <div className="hero-scrim" />
        <div className="container hero-grid">
          <div className="hero-copy">
            <div className="eyebrow on-dark rise">{h.hero.eyebrow}</div>
            <h1 className="h1 rise rise-2" style={{ marginTop: 18, maxWidth: "15ch" }}>{h.hero.title}</h1>
            <p className="lead rise rise-3" style={{ color: "#cdd8e2", marginTop: 22, maxWidth: "46ch" }}>{h.hero.sub}</p>
            <div className="rise rise-4" style={{ display: "flex", gap: 14, marginTop: 30, flexWrap: "wrap" }}>
              <Btn href={h.hero.primary_cta.href} testid="hero-cta">{h.hero.primary_cta.label}</Btn>
              <Btn href={h.hero.secondary_cta.href} variant="ghost" dark testid="hero-secondary">{h.hero.secondary_cta.label}</Btn></div>
            <div className="hero-markers rise rise-4">{h.hero.markers.map((m) => <span key={m} className="hero-pill">{m}</span>)}</div>
          </div>
          <HeroCascade />
        </div>
      </section>

      {/* 02 WHY IT MAY MATTER — image-led */}
      <section className="section dark core"><div className="container">
        <div className="grid g2 core-top" style={{ gap: 48, alignItems: "center", marginBottom: 48 }}>
          <div><div className="eyebrow on-dark">{h.benefits.eyebrow}</div>
            <h2 className="h2" style={{ margin: "12px 0 16px", maxWidth: "18ch" }}>{h.benefits.title}</h2>
            <p className="lead">{h.benefits.lead}</p></div>
          <div className="core-media"><img src={h.benefits.image} alt={h.benefits.imageAlt} loading="lazy" /><span className="core-media-grad" /></div>
        </div>
        <div className="core-list">{h.benefits.items.map((b, idx) => (
          <a key={b.n} href={`/value/${b.slug}`} className="benrow benrow-link" data-testid={`benefit-${idx}`}>
            <span className="benrow-n">{b.n}</span>
            <span className="benrow-name"><img src={BEN_ICON[idx]} alt="" className="gicon" />{b.name}</span>
            <span className="benrow-text">{b.text}</span>
            <span className="benrow-go"><ArrowUpRight size={18} /></span></a>))}</div>
      </div></section>

      {/* 03 PRINCIPLE */}
      <Principle />

      {/* 04 APPLICATIONS (editorial gallery) */}
      <section className="section"><div className="container">
        <div style={{ marginBottom: 28, maxWidth: "60ch" }}><div className="eyebrow">Where HydraGEN™ creates value</div>
          <h2 className="h2" style={{ margin: "12px 0 14px" }}>Wherever diesel engines do the hard work.</h2>
          <p className="lead">Explore by industry or by the equipment you run — the same technology, engineered per application.</p></div>
        <div className="appgallery">
          {inds.map((i2, idx) => (<a key={i2.slug} href={`/industries/${i2.slug}`} className={`appcell cell-${idx}`} data-testid={`home-ind-${i2.slug}`}>
            <img src={i2.image} alt={i2.alt} loading="lazy" /><span className="appcell-grad" />
            <span className="appcell-cap"><span className="tag" style={{ color: "#fff", borderColor: "rgba(255,255,255,.4)" }}>{i2.priority}</span><b>{i2.name}</b><ArrowUpRight size={18} /></span></a>))}
        </div>
        <div style={{ marginTop: 24 }}><Btn href="/applications" variant="ghost" testid="home-apps">Explore all applications</Btn></div>
      </div></section>

      {/* 05 PRODUCT FAMILY STAGE — full-width showcase */}
      <section className="section paper2 famstage"><div className="container">
        <div className="fam-intro">
          <div className="fam-intro-copy">
            <div className="eyebrow">The HydraGEN™ product family</div>
            <h2 className="h2" style={{ margin: "12px 0 14px", maxWidth: "20ch" }}>One technology. A scalable family of units.</h2>
            <p className="lead" style={{ maxWidth: "60ch" }}>From the R-series for on/off-highway engines to the C-series for large off-highway assets — the right unit is confirmed by an application assessment.</p>
          </div>
          <div className="fam-intro-motif" aria-hidden="true"><img src="/assets/principle/molecules.png" alt="" loading="lazy" /></div>
        </div>
        <ProductFamilyStage products={prods} />
        <div style={{ marginTop: 30, textAlign: "center" }}><Btn href="/products" variant="ghost" testid="home-products">See the full product family</Btn></div>
      </div></section>

      {/* 06 OPERATIONAL & COMMERCIAL VALUE — deep-dive */}
      <section className="section"><div className="container">
        <div className="eyebrow">Operational & commercial value</div>
        <h2 className="h2" style={{ margin: "12px 0 10px", maxWidth: "22ch" }}>Value organised around what operators care about.</h2>
        <p className="lead" style={{ marginBottom: 28 }}>Each value area opens into a deeper story — why it matters to an operator, not just a headline.</p>
        <ValueDeepDive items={value.narratives} />
      </div></section>

      {/* 07 CARBON VALUE — with monetisation flow */}
      <section className="section graphite carbon"><div className="container">
        <div className="grid g2" style={{ gap: 48, alignItems: "center" }}>
          <div>
            <div className="eyebrow on-dark">{h.carbon.eyebrow}</div>
            <h2 className="h2" style={{ margin: "14px 0 18px" }}>{h.carbon.title}</h2>
            <p className="lead">{h.carbon.text}</p>
            <div style={{ marginTop: 24 }}><Btn href="/why-hydragen" variant="ghost" dark testid="carbon-cta">Explore the measured path</Btn></div>
          </div>
          <div className={`carbon-flow ${carbonSeen ? "in" : ""}`} ref={carbonRef} data-testid="carbon-flow">
            {h.carbon.flow.map((f, idx) => { const ic = [IC("H05_analytics"), IC("H06_carbon_readiness"), IC("H07_carbon_credits")][idx]; return (
              <Fragment key={f.k}>
                <div className={`cflow-node n${idx}`} tabIndex={0} role="group" aria-label={f.k} data-testid={`cflow-node-${idx}`}><span className="cflow-ic"><img src={ic} alt="" className="gicon lg" /></span><b>{f.k}</b><span>{f.t}</span></div>
                {idx < h.carbon.flow.length - 1 && <span className="cflow-arrow" aria-hidden="true"><ArrowRight size={20} /></span>}
              </Fragment>); })}
            <span className="cflow-note">Potential only — eligibility, methodology, verification and any monetisation are assessed case by case. No credits, revenue or outcome are promised.</span>
          </div>
        </div>
      </div></section>

      {/* 08 PROJECT APPROACH */}
      <ProjectApproach />

      {/* 09 TECHNOLOGY / HYDRALYTICA TRUST */}
      <section className="section dark"><div className="container"><div className="grid g2" style={{ gap: 48, alignItems: "center" }}>
        <div className="hl-anchor"><img src={hydralytica.image} alt={hydralytica.imageAlt} loading="lazy" /></div>
        <div><div className="eyebrow on-dark">Engineering trust</div>
          <h2 className="h2" style={{ margin: "12px 0 16px", maxWidth: "18ch" }}>Engineered technology, made visible.</h2>
          <p className="lead">HydraGEN™ is an engineered retrofit — and its operation is made visible through {technology.monitoring.name.replace(" monitoring", "")}, dynaCERT's data and monitoring platform for connected operational visibility, analytics and reporting.</p>
          <img src={LOGO.hydralytica} alt="HydraLytica™" style={{ height: 30, margin: "22px 0 18px" }} />
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}><Btn href="/hydralytica" variant="ghost" dark testid="home-tech">Explore HydraLytica™</Btn></div>
        </div>
      </div></div></section>

      {/* 10 GLOBAL / COMPANY TRUST + MARKET ACCESS */}
      <section className="section presence"><img src={company.mapImage} alt={company.mapAlt} className="presence-map" /><div className="container" style={{ position: "relative", textAlign: "center" }}>
        <Globe2 size={30} color="var(--brand)" style={{ margin: "0 auto 14px" }} />
        <div className="eyebrow">International reach</div>
        <h2 className="h2" style={{ margin: "12px auto 14px", maxWidth: "22ch" }}>An engineering-led company behind serious industrial projects.</h2>
        <div className="small" style={{ marginBottom: 22 }}>{company.offices.join(" · ")}</div>
        <p className="market-bridge">{investors.bridge}</p>
        <div className="market-signal" data-testid="market-signal">
          <span className="market-lead">{market.eyebrow}</span>
          <span className="market-tickers">{market.listings.map((l) => <span key={l.exch} className="market-tick"><b>{l.exch}</b> {l.ticker}</span>)}</span>
          <a href={market.cta.href} className="market-link" data-testid="market-ir-link">{market.cta.label}<ArrowUpRight size={14} /></a>
        </div>
        <div style={{ marginTop: 22 }}><Btn href="/company" variant="ghost" testid="home-company">About dynaCERT</Btn></div>
      </div></section>

      <CtaBand />
    </>
  );
}

/* ---------------- Technology ---------------- */
const Technology = () => (
  <>
    <PageHead crumb="Home / Technology" eyebrow="Technology" title={technology.title} lead={technology.intro} />
    <section className="section paper2"><div className="container"><div className="grid g2" style={{ gap: 48, alignItems: "center" }}>
      <div className="diagram-frame"><img src={technology.heroImage} alt={technology.heroAlt} loading="lazy" /></div>
      <div><div className="eyebrow">The principle</div><h2 className="h2" style={{ margin: "12px 0 22px", maxWidth: "18ch" }}>Produced on demand — nothing stored.</h2>
        {technology.features.map((f) => <div key={f.name} style={{ marginBottom: 18 }}><div className="h3" style={{ fontSize: "1.12rem" }}>{f.name}</div><p style={{ color: "#3f4f5c", marginTop: 6 }}>{f.text}</p></div>)}</div>
    </div></div></section>
    <Principle />
    <section className="section"><div className="container"><div className="grid g2" style={{ gap: 48 }}>
      <div><div className="eyebrow">Safety & integrity</div><h2 className="h2" style={{ margin: "12px 0 18px" }}>{safety.title}</h2>
        {safety.points.map((p, idx) => <div key={idx} className="check"><Activity size={20} color="var(--brand)" style={{ flexShrink: 0 }} /><span>{p}</span></div>)}</div>
      <div className="card"><img src={LOGO.hydralytica} alt="HydraLytica™" style={{ height: 30, marginBottom: 14 }} /><div className="h3">{technology.monitoring.name}</div>
        <p style={{ color: "#3f4f5c", marginTop: 10 }}>{technology.monitoring.text}</p><a href="/hydralytica" className="small" style={{ color: "var(--brand)", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, marginTop: 10 }} data-testid="tech-hydralytica">Explore HydraLytica™ <ArrowUpRight size={14} /></a></div>
    </div></div></section>
    <section className="section"><div className="container">
      <div className="eyebrow">Thermodynamic effects</div>
      <h2 className="h2" style={{ margin: "12px 0 20px", maxWidth: "20ch" }}>Why more complete combustion matters.</h2>
      <div className="grid g2">{technology.effects.map((e) => <div key={e} className="check"><Zap size={18} color="var(--brand)" style={{ flexShrink: 0 }} /><span>{e}</span></div>)}</div>
      <div className="deep-links" style={{ marginTop: 24, maxWidth: 560 }}><a href="/hydralytica" className="deep-link"><span>Data & monitoring — HydraLytica™</span><ArrowUpRight size={15} /></a><a href="/value/fuel-efficiency" className="deep-link"><span>Fuel efficiency potential</span><ArrowUpRight size={15} /></a><a href="/applications" className="deep-link"><span>Where it fits — Applications</span><ArrowUpRight size={15} /></a></div>
    </div></section>
    <CtaBand />
  </>
);

/* ---------------- Products ---------------- */
const ProductsIndex = () => (
  <>
    <PageHead crumb="Home / Products" eyebrow="Products" title="One technology, multiple solutions."
      lead="The scalable HydraGEN™ unit family retrofits existing diesel engines. The right unit is confirmed by an application assessment — engine, load, environment and goals." />
    <section className="section paper2"><div className="container">
      <div className="grid g4">{listProducts().map((p) => { const s = getSeries(p.series); return (
        <a key={p.slug} href={`/products/${p.slug}`} className="card card-pad0" data-testid={`product-${p.slug}`}>
          <div className="card-media product ar-4-3"><img src={p.image} alt={p.alt} loading="lazy" /></div>
          <div style={{ padding: "1.2rem 1.4rem 1.5rem" }}><div className="tag">{s.series}</div>
            <div className="font-display" style={{ fontWeight: 800, fontSize: "1.8rem", margin: "12px 0 6px" }}>{p.name}</div>
            <div className="small">{s.band}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--brand)", marginTop: 16, fontWeight: 700, fontSize: ".9rem" }}>Details <ChevronRight size={15} /></div></div>
        </a>); })}</div>
      <p className="notice" style={{ marginTop: 30 }}>The right HydraGEN™ unit is confirmed for your engine and duty cycle during an application assessment.</p>
    </div></section>
    <CtaBand />
  </>
);
const ProductDetail = ({ slug }) => {
  const p = getProduct(slug); if (!p) return <NotFound />; const s = getSeries(p.series);
  return (<>
    <PageHead crumb={`Home / Products / ${p.name}`} eyebrow={`${s.series} · HydraGEN™`} title={p.name} lead={p.positioning} />
    <section className="section paper2"><div className="container"><div className="grid g2" style={{ gap: 48, alignItems: "start" }}>
      <div><div className="diagram-frame product"><img src={p.image} alt={p.alt} loading="lazy" data-testid="pd-media" /></div>
        <div className="mega-col-title" style={{ marginTop: 28 }}>Positioning (series-level)</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
          {[["Series", s.series], ["Engine range", s.band], ["Integration", "Retrofit to existing engine"], ["Water", "Distilled water only"]].map((r) => (
            <tr key={r[0]}><td style={{ padding: "12px 0", borderBottom: "1px solid var(--line)", color: "var(--steel)", fontSize: ".9rem" }}>{r[0]}</td>
              <td style={{ padding: "12px 0", borderBottom: "1px solid var(--line)", fontWeight: 700, textAlign: "right" }}>{r[1]}</td></tr>))}
        </tbody></table>
        <p className="notice" style={{ marginTop: 24 }}>Exact unit specifications are confirmed for your engine and duty cycle during the application assessment.</p></div>
      <div className="card" style={{ padding: "2.2rem" }}><div className="tag">Is this the right unit?</div>
        <h3 className="h3" style={{ margin: "14px 0 10px" }}>Check technical fit for your equipment</h3>
        <p style={{ color: "#3f4f5c" }}>Share your engine and duty cycle and our engineering team will confirm the candidate unit.</p>
        <div style={{ display: "flex", gap: 12, marginTop: 22, flexWrap: "wrap" }}><Btn href="/contact" testid="pd-cta">Check technical fit</Btn><Btn href="/applications" variant="ghost" testid="pd-apps">See applications</Btn></div></div>
    </div></div></section>
    <CtaBand />
  </>);
};

/* ---------------- Applications & Industries ---------------- */
const ApplicationsIndex = () => (
  <>
    <PageHead crumb="Home / Applications" eyebrow="Applications" title="Browse by industry or by equipment."
      lead="Two ways in, one engineered technology. Find your sector or the specific equipment you operate." />
    <section className="section paper2"><div className="container">
      <div className="mega-col-title">By industry</div>
      <div className="appgallery" style={{ marginBottom: 44 }}>{listIndustries().map((i, idx) => (
        <a key={i.slug} href={`/industries/${i.slug}`} className={`appcell cell-${idx}`} data-testid={`appidx-ind-${i.slug}`}>
          <img src={i.image} alt={i.alt} loading="lazy" /><span className="appcell-grad" />
          <span className="appcell-cap"><span className="tag" style={{ color: "#fff", borderColor: "rgba(255,255,255,.4)" }}>{i.priority}</span><b>{i.name}</b><ArrowUpRight size={18} /></span></a>))}</div>
      <div className="mega-col-title">By equipment</div>
      <div className="grid g3">{listApplications().map((a) => (
        <a key={a.slug} href={`/applications/${a.slug}`} className="card card-pad0" data-testid={`appidx-app-${a.slug}`}>
          <div className="card-media ar-3-2"><img src={a.image} alt={a.alt} loading="lazy" /></div>
          <div style={{ padding: "1rem 1.2rem 1.3rem" }}><div className="h3" style={{ fontSize: "1.12rem" }}>{a.name}</div><p className="small" style={{ marginTop: 8 }}>{a.context}</p></div></a>))}</div>
    </div></section>
    <CtaBand />
  </>
);
const IndustryDetail = ({ slug }) => {
  const i = getIndustry(slug); if (!i) return <NotFound />; const I = IND_ICON[i.slug] || Ship;
  return (<>
    <PageHead crumb={`Home / Applications / ${i.name}`} eyebrow={i.priority} title={i.name} lead={i.problem} />
    <section className="section paper2"><div className="container">
      <div className="diagram-frame wide photo"><img src={i.image} alt={i.alt} loading="lazy" data-testid="ind-media" /></div>
      <div className="grid g2" style={{ gap: 48, alignItems: "start", marginTop: 44 }}>
        <div><div className="mega-col-title">Typical equipment</div>{i.equipment.map((e) => <div key={e} className="check"><Wrench size={18} color="var(--brand-2)" style={{ flexShrink: 0 }} /><span>{e}</span></div>)}</div>
        <div><div className="mega-col-title">Value categories (assessed per operation)</div>{i.values.map((v) => <div key={v} className="check"><Gauge size={18} color="var(--brand)" style={{ flexShrink: 0 }} /><span>{v}</span></div>)}
          <p className="notice" style={{ marginTop: 22 }}>Our Project Approach explains how outcomes are assessed and measured for operations like yours.</p></div>
      </div>
    </div></section>
    <section className="section"><div className="container" style={{ textAlign: "center" }}><I size={32} color="var(--brand)" style={{ margin: "0 auto" }} />
      <h2 className="h2" style={{ margin: "18px auto", maxWidth: "20ch" }}>Assess {i.name.toLowerCase()} equipment for HydraGEN™.</h2><Btn href="/contact" testid="ind-cta">Discuss Your Application</Btn></div></section>
  </>);
};
const ApplicationDetail = ({ slug }) => {
  const a = getApplication(slug); if (!a) return <NotFound />; const ind = getIndustry(a.industry); const s = getSeries(a.series);
  return (<>
    <PageHead crumb={`Home / Applications / ${a.name}`} eyebrow={ind ? ind.name : "Application"} title={a.name} lead={a.context} />
    <section className="section paper2"><div className="container">
      <div className="diagram-frame wide photo"><img src={a.image} alt={a.alt} loading="lazy" data-testid="ad-media" /></div>
      <div className="grid g3" style={{ marginTop: 44 }}>
        <div className="card"><div className="tag">HydraGEN™ fit</div><p style={{ marginTop: 12, color: "#3f4f5c" }}>A retrofit to the existing engine; on-demand H₂/O₂ introduced via the air-intake path.</p></div>
        <div className="card"><div className="tag">Candidate unit</div><p style={{ marginTop: 12, color: "#3f4f5c" }}>{s.series} — confirmed by assessment, not guaranteed in advance.</p><a href="/products" className="small" style={{ color: "var(--brand)", fontWeight: 700 }}>See products →</a></div>
        <div className="card"><div className="tag">What we assess</div><p style={{ marginTop: 12, color: "#3f4f5c" }}>Duty cycle, environment, service access, water quality and measurement method.</p></div>
      </div>
      {ind && <div style={{ marginTop: 28 }}><a href={`/industries/${ind.slug}`} className="btn btn-ghost" data-testid="ad-industry">In industry: {ind.name}</a></div>}
    </div></section>
    <CtaBand />
  </>);
};

/* ---------------- Why HydraGEN ---------------- */
const WhyHydraGEN = () => (
  <>
    <PageHead crumb="Home / Why HydraGEN™" eyebrow="Why HydraGEN™" title={value.title} lead={value.intro} />
    <section className="section paper2"><div className="container"><ValueDeepDive items={value.narratives} /></div></section>
    <ProjectApproach />
    <CtaBand />
  </>
);

/* ---------------- Resources ---------------- */
const Resources = () => (
  <>
    <PageHead crumb="Home / Resources" eyebrow="Resources & Evidence" title={resources.title} lead={resources.intro} />
    <section className="section paper2"><div className="container">
      <div className="res-cats">{resources.categories.map((c) => <span key={c} className="res-cat">{c}</span>)}</div>
      {resources.evidence_available && <div className="grid g3" data-testid="evidence-grid" style={{ margin: "30px 0" }} />}
      <div className="grid g2" style={{ marginTop: 30 }}>{resources.items.map((r) => <a key={r.title} href={r.href} className="card" data-testid="resource-item">
        <div className="tag">{r.type}</div><div className="h3" style={{ marginTop: 12, fontSize: "1.2rem" }}>{r.title}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--brand)", marginTop: 14, fontWeight: 700, fontSize: ".9rem" }}>Open <ArrowUpRight size={15} /></div></a>)}</div>
    </div></section>
    <CtaBand />
  </>
);

/* ---------------- Company / Investors ---------------- */
function Company() {
  const mgmt = (leadership.groups?.find((g) => g.id === "management")?.people || []).slice(0, 4);
  return (
    <>
      <PageHead crumb="Home / Company" eyebrow="Company" title={company.title} lead={company.intro} tight />
      {/* WHO WE ARE */}
      <section className="section paper2" style={{ paddingTop: "2rem" }}><div className="container">
        <div className="grid g3">{company.points.map((p) => <div key={p.name} className="card"><Building2 size={22} color="var(--brand)" />
          <div className="h3" style={{ marginTop: 12, fontSize: "1.15rem" }}>{p.name}</div><p style={{ color: "#3f4f5c", marginTop: 10 }}>{p.text}</p></div>)}</div>
      </div></section>
      {/* ENGINEERING + COMMERCIAL MISSION */}
      <section className="section"><div className="container"><div className="grid g2" style={{ gap: 48, alignItems: "center" }}>
        <div>
          <div className="eyebrow">Why we exist</div>
          <h2 className="h2" style={{ margin: "12px 0 16px", maxWidth: "18ch" }}>Value from the equipment operators already own.</h2>
          <p className="lead">dynaCERT engineers HydraGEN™ Technology as a retrofit for existing internal-combustion diesel equipment — supporting cleaner combustion and connected operational visibility through HydraLytica™, without replacing the assets operators depend on.</p>
          <div style={{ marginTop: 22, display: "flex", gap: 14, flexWrap: "wrap" }}><Btn href="/technology" variant="ghost" testid="cmp-tech">Explore the technology</Btn><Btn href="/applications" variant="ghost" testid="cmp-apps">See applications</Btn></div>
        </div>
        <div className="cmp-media"><img src="/assets/apps/power-generation.jpg" alt="Industrial diesel power-generation equipment at dusk" loading="lazy" data-testid="company-media" /></div>
      </div></div></section>
      {/* PEOPLE TEASER */}
      <section className="section paper2"><div className="container">
        <div className="cmp-people-head">
          <div><div className="eyebrow">People</div><h2 className="h2" style={{ margin: "10px 0 0", fontSize: "1.7rem" }}>Engineering, technology and people.</h2></div>
          <Btn href="/company/leadership" variant="ghost" testid="cmp-team">Meet the team</Btn>
        </div>
        <div className="cmp-people">{mgmt.map((p) => <a key={p.slug} href="/company/leadership" className="cmp-person" data-testid={`cmp-person-${p.slug}`}><img src={p.photo} alt={p.name} loading="lazy" /><div className="cmp-person-meta"><b>{p.name}</b><span>{p.role}</span></div></a>)}</div>
      </div></section>
      {/* GLOBAL */}
      <section className="section presence"><img src={company.mapImage} alt={company.mapAlt} className="presence-map" /><div className="container" style={{ position: "relative", textAlign: "center" }}>
        <Globe2 size={30} color="var(--brand)" style={{ margin: "0 auto 14px" }} /><div className="eyebrow">International reach</div>
        <h2 className="h2" style={{ margin: "12px auto 14px", maxWidth: "20ch" }}>Supported from Canada and Germany.</h2><div className="small">{company.offices.join(" · ")}</div>
        <div style={{ marginTop: 18 }}><Btn href="/company/global-presence" variant="ghost" testid="cmp-global">Global presence</Btn></div>
      </div></section>
      <CtaBand />
    </>
  );
}
const IR_ICON = { overview: LineChart, reports: FileText, news: Newspaper, presentations: Presentation, governance: ShieldCheck, contact: Mail };
const Investors = () => (
  <>
    <PageHead crumb="Home / Investors" eyebrow="Investor Relations · Secondary" title={investors.title} lead={investors.intro} tight />
    <section className="section paper2"><div className="container">
      <p className="lead" style={{ marginTop: -8, marginBottom: 18 }}>{investors.bridge}</p>
      <div className="ir-market" data-testid="ir-market">
        <div className="ir-market-col listings" data-testid="ir-listings">
          <span className="ir-market-lead">Where it trades</span>
          <div className="ir-market-rows">{investors.listings.map((l) => <div key={l.exch} className="ir-market-row"><span className="ir-exch">{l.exch}</span><b>{l.ticker}</b></div>)}</div>
        </div>
        {investors.identifiers && <div className="ir-market-col ids" data-testid="ir-identifiers">
          <span className="ir-market-lead">How it's identified</span>
          <div className="ir-market-rows">{investors.identifiers.map((l) => <div key={l.k} className="ir-market-row"><span className="ir-exch">{l.k}</span><b>{l.v}</b></div>)}</div>
        </div>}
      </div>
      <div className="grid g3">{investors.sections.map((s) => { const Ic = IR_ICON[s.slug] || LineChart; return (
        <a key={s.slug} href={`/investors/${s.slug}`} className="card" data-testid={`ir-${s.slug}`}><Ic size={22} color="var(--brand)" /><div className="h3" style={{ marginTop: 12, fontSize: "1.12rem" }}>{s.name}</div><p style={{ color: "#3f4f5c", marginTop: 8 }}>{s.text}</p></a>); })}</div>
      <p className="notice" style={{ marginTop: 26 }}>{investors.identifiersNote}</p>
      <div style={{ marginTop: 20, display: "flex", gap: 14, flexWrap: "wrap" }}>{investors.legacy.map((l) => <a key={l.label} href={l.href} target="_blank" rel="noreferrer" className="btn btn-ghost" data-testid="ir-legacy">{l.label}<ExternalLink size={14} /></a>)}</div>
      <p className="small" style={{ marginTop: 16 }}>{investors.note}</p>
    </div></section>
  </>
);
const IRContact = () => (
  <>
    <PageHead crumb="Home / Investors / IR Contact" eyebrow="Investor Relations" title="IR Contact" lead="For investor enquiries, please contact dynaCERT's investor relations." />
    <section className="section paper2"><div className="container"><div className="grid g2" style={{ gap: 48, alignItems: "start" }}>
      <div className="card"><div className="tag">{irContact.org}</div>
        <div className="h3" style={{ marginTop: 12 }}>{irContact.name}</div>
        <div className="check" style={{ marginTop: 10 }}><Mail size={18} color="var(--brand)" /><a href={`mailto:${irContact.email}`} style={{ color: "var(--brand)", fontWeight: 700 }} data-testid="ir-email">{irContact.email}</a></div>
        <div className="check"><Phone size={18} color="var(--brand)" /><span>{irContact.phone}</span></div>
        <div className="check"><MapPin size={18} color="var(--brand)" /><span>{irContact.address}</span></div></div>
      <div><p style={{ color: "#3f4f5c" }}>{irContact.euOffice}</p>{irContact.note && <p className="notice" style={{ marginTop: 18 }}>{irContact.note}</p>}</div>
    </div></div></section>
  </>
);
const IRSection = ({ slug }) => {
  const s = investors.sections.find((x) => x.slug === slug); if (!s) return <NotFound />;
  if (slug === "contact") return <IRContact />;
  return (<>
    <PageHead crumb={`Home / Investors / ${s.name}`} eyebrow="Investor Relations" title={s.name} lead={s.text} />
    <section className="section paper2"><div className="container">
      <p className="notice">This section links to dynaCERT's existing authoritative disclosure infrastructure. Regulated historical content is not duplicated here.</p>
      <div style={{ marginTop: 20, display: "flex", gap: 14, flexWrap: "wrap" }}>{investors.legacy.map((l) => <a key={l.label} href={l.href} target="_blank" rel="noreferrer" className="btn btn-ghost" data-testid="ir-sec-legacy">{l.label}<ExternalLink size={14} /></a>)}
        <a href="/investors" className="btn btn-ghost" data-testid="ir-back">Back to IR overview</a></div>
    </div></section>
  </>);
};
const ValuePage = ({ slug }) => {
  const b = getBenefitPage(slug); if (!b) return <NotFound />;
  return (<>
    <PageHead crumb={`Home / Why HydraGEN™ / ${b.title}`} eyebrow={b.eyebrow} title={b.title} lead={b.lead} />
    <section className="section paper2"><div className="container"><div className="grid g2" style={{ gap: 48, alignItems: "start" }}>
      <div>{b.body.map((p, i) => <p key={i} className="lead" style={{ marginBottom: 18 }}>{p}</p>)}
        <div className="valnar-tags" style={{ marginTop: 8 }}>{b.points.map((pt) => <span key={pt} className="tag">{pt}</span>)}</div></div>
      <div className="card"><div className="mega-col-title">Explore related</div>
        {b.links.map((l) => <a key={l.label} href={l.href} className="deep-link" data-testid="value-related"><span>{l.label}</span><ArrowUpRight size={15} /></a>)}
        <div style={{ marginTop: 18 }}><Btn href="/contact" testid="value-page-cta">Discuss Your Application</Btn></div></div>
    </div></div></section>
    <CtaBand />
  </>);
};
const HydraLytica = () => (
  <>
    <PageHead crumb="Home / HydraLytica™" eyebrow={hydralytica.eyebrow} title={hydralytica.title} lead={hydralytica.intro} />
    <section className="section paper2"><div className="container">
      <div className="hl-hero" data-testid="hl-device"><img src={hydralytica.image} alt={hydralytica.imageAlt} loading="lazy" /></div>
      <div className="hl-cap-head" style={{ marginTop: 56 }}>
        <div className="eyebrow">{hydralytica.capabilitiesTitle}</div>
        <h2 className="h2" style={{ margin: "12px 0 8px", maxWidth: "20ch" }}>Connected operation, made measurable.</h2>
      </div>
      <div className="grid g3 hl-cap-grid" style={{ marginTop: 26 }}>
        {hydralytica.capGrid.map((c) => <div key={c.k} className="card hl-cap" data-testid="hl-cap"><img src={IC(HL_ICON[c.icon] || c.icon)} alt="" className="gicon lg" /><div className="h3" style={{ fontSize: "1.12rem", marginTop: 12 }}>{c.k}</div><p style={{ color: "#3f4f5c", marginTop: 6 }}>{c.t}</p></div>)}
      </div>
      <div className="grid g2" style={{ gap: 48, marginTop: 44 }}>
        <div className="card"><div className="tag">HydraGEN™ + data</div><p style={{ marginTop: 12, color: "#3f4f5c" }}>{hydralytica.relationship}</p></div>
        <div className="card"><div className="tag">Carbon context</div><p style={{ marginTop: 12, color: "#3f4f5c" }}>{hydralytica.carbon}</p></div>
      </div>
      <p className="notice" style={{ marginTop: 24 }}>{hydralytica.note}</p>
      <div style={{ marginTop: 24 }}><Btn href="/contact" testid="hl-cta">Discuss Your Application</Btn></div>
    </div></section>
    <CtaBand />
  </>
);
const Initials = ({ name }) => <span className="ppl-ini" aria-hidden="true">{name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("")}</span>;
function Leadership() {
  const [open, setOpen] = useState(null);
  const groups = leadership.groups || [];
  const person = open;
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    if (person) { document.addEventListener("keydown", onKey); document.body.style.overflow = "hidden"; }
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [person]);
  return (
    <>
      <PageHead crumb="Home / Company / Leadership" eyebrow={leadership.eyebrow} title={leadership.title} lead={leadership.intro} />
      <section className="section paper2"><div className="container">
        {groups.map((g) => (
          <div key={g.id} className="team-group" data-testid={`team-group-${g.id}`}>
            <div className="team-group-head"><h2 className="h2" style={{ fontSize: "1.6rem" }}>{g.title}</h2><p className="small" style={{ maxWidth: "56ch", marginTop: 6 }}>{g.blurb}</p></div>
            <div className="team-grid">
              {g.people.map((p) => {
                const hasBio = p.bio && p.bio.trim().length > 0;
                return (
                  <div key={p.slug} className={`team-card ${hasBio ? "clickable" : ""}`} data-testid={`team-${p.slug}`}
                    role={hasBio ? "button" : undefined} tabIndex={hasBio ? 0 : undefined}
                    onClick={hasBio ? () => setOpen(p) : undefined}
                    onKeyDown={hasBio ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(p); } } : undefined}>
                    <div className="team-photo">{p.photo ? <img src={p.photo} alt={`${p.name}, ${p.role}`} loading="lazy" /> : <Initials name={p.name} />}</div>
                    <div className="team-meta">
                      <div className="team-name">{p.name}</div>
                      <div className="team-role">{p.role}</div>
                      <p className="team-intro">{p.intro}</p>
                      {hasBio && <span className="team-readbio" data-testid={`team-readbio-${p.slug}`}>Read bio <ArrowUpRight size={13} /></span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div></section>
      {person && (
        <div className="team-modal-overlay" onClick={() => setOpen(null)} data-testid="team-modal">
          <div className="team-modal" role="dialog" aria-modal="true" aria-label={person.name} onClick={(e) => e.stopPropagation()}>
            <button className="team-modal-close" aria-label="Close" onClick={() => setOpen(null)} data-testid="team-modal-close"><X size={20} /></button>
            <div className="team-modal-grid">
              <div className="team-modal-photo">{person.photo ? <img src={person.photo} alt={`${person.name}, ${person.role}`} /> : <Initials name={person.name} />}</div>
              <div className="team-modal-body">
                <div className="team-role" style={{ color: "var(--brand)" }}>{person.role}</div>
                <h3 className="h2" style={{ fontSize: "1.5rem", margin: "4px 0 14px" }}>{person.name}</h3>
                {person.bio.split("\n\n").map((para, i) => <p key={i} className="team-modal-para">{para}</p>)}
              </div>
            </div>
          </div>
        </div>
      )}
      <CtaBand />
    </>
  );
}
function GlobalPresence() {
  const [act, setAct] = useState(0);
  const pos = [{ left: "26%", top: "40%" }, { left: "49%", top: "36%" }, { left: "72%", top: "52%" }];
  const locs = globalPresence.locations;
  return (
    <>
      <PageHead crumb="Home / Company / Global Presence" eyebrow={globalPresence.eyebrow} title={globalPresence.title} lead={globalPresence.intro} tight />
      <section className="section paper2 gp-tight"><div className="container"><div className="grid g2" style={{ gap: 40, alignItems: "start" }}>
        <div className="gp-map-wrap" data-testid="gp-map">
          <img src={company.mapImage} alt={company.mapAlt} loading="lazy" />
          {locs.map((l, idx) => <button key={l.name} className={`gp-marker ${act === idx ? "on" : ""}`} style={pos[idx] || pos[0]} onMouseEnter={() => setAct(idx)} onFocus={() => setAct(idx)} onClick={() => setAct(idx)} aria-label={l.name} data-testid={`gp-marker-${idx}`} />)}
        </div>
        <div className="gp-list" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {locs.map((l, idx) => <button key={l.name} className={`card gp-loc ${act === idx ? "on" : ""}`} onMouseEnter={() => setAct(idx)} onFocus={() => setAct(idx)} onClick={() => setAct(idx)} data-testid="gp-loc"><MapPin size={18} color={act === idx ? "var(--brand-2)" : "var(--brand)"} /><div className="tag" style={{ marginTop: 8 }}>{l.type}</div><div className="h3" style={{ fontSize: "1.05rem", marginTop: 6 }}>{l.name}</div><p style={{ color: "#3f4f5c", marginTop: 6 }}>{l.detail}</p></button>)}
        </div>
      </div>
      <p className="notice" style={{ marginTop: 24 }}>{globalPresence.note}</p>
      </div></section>
      <CtaBand />
    </>
  );
}
const Careers = () => (
  <>
    <PageHead crumb="Home / Company / Careers" eyebrow={careers.eyebrow} title={careers.title} lead={careers.intro} />
    <section className="section paper2"><div className="container">
      <div className="mega-col-title">Areas of expertise</div>
      <div className="grid g3">{careers.areas.map((a) => <div key={a} className="card" data-testid="career-area"><Briefcase size={20} color="var(--brand)" /><div className="h3" style={{ fontSize: "1.1rem", marginTop: 10 }}>{a}</div></div>)}</div>
      <p className="notice" style={{ marginTop: 24 }}>{careers.note}</p>
      <div style={{ marginTop: 20 }}><Btn href={careers.cta.href} testid="careers-cta">{careers.cta.label}</Btn></div>
    </div></section>
    <CtaBand />
  </>
);
const About = () => (
  <>
    <PageHead crumb="Home / Company / About" eyebrow={about.eyebrow} title={about.title} lead={about.story[0]} />
    <section className="section paper2"><div className="container"><div className="grid g2" style={{ gap: 48, alignItems: "center" }}>
      <div className="diagram-frame wide photo"><img src={company.image} alt={company.alt} loading="lazy" /></div>
      <div>{about.story.slice(1).map((p, i) => <p key={i} className="lead" style={{ marginBottom: 16 }}>{p}</p>)}
        <p className="small">{about.peopleNote}</p>
        <div className="deep-links" style={{ marginTop: 16 }}>{about.links.map((l) => <a key={l.label} href={l.href} className="deep-link" data-testid="about-link"><span>{l.label}</span><ArrowUpRight size={15} /></a>)}</div></div>
    </div></div></section>
    <CtaBand />
  </>
);
const LegalPage = ({ slug }) => {
  const r = legal.routes.find((x) => x.slug === slug); if (!r) return <NotFound />;
  return (<>
    <PageHead crumb={`Home / Legal / ${r.title}`} eyebrow={legal.eyebrow} title={r.title} lead={r.juris || "dynaCERT corporate."} />
    <section className="section paper2"><div className="container">
      {r.juris && <p className="small" style={{ marginTop: 16 }}>Applicability: {r.juris}</p>}
      <div style={{ marginTop: 20 }}><Btn href="/contact" variant="ghost" testid="legal-contact">Contact dynaCERT</Btn></div>
    </div></section>
  </>);
};
const NotFound = () => (<section className="section"><div className="container" style={{ textAlign: "center" }}>
  <h1 className="h1">Page not found</h1><p className="lead" style={{ margin: "16px auto" }}>The page you're looking for isn't available. Please use the navigation above or return to the homepage.</p><Btn href="/" testid="nf-home">Back to home</Btn></div></section>);

/* ---------------- Contact ---------------- */
function Contact() {
  const [state, setState] = useState("idle"); // idle | loading | sent | error
  const [err, setErr] = useState("");
  const [token, setToken] = useState("");
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/token`).then((r) => r.json()).then((d) => setToken(d.token || "")).catch(() => {});
  }, []);
  const onSubmit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const payload = {
      name: f.get("name") || "", company: f.get("company") || "", email: f.get("email") || "",
      country: f.get("country") || "", industry: f.get("industry") || "", objective: f.get("objective") || "",
      engine: f.get("engine") || "", units: f.get("units") || "", message: f.get("message") || "",
      website: f.get("website") || "", token, consent: f.get("consent") === "on",
    };
    setState("loading"); setErr("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Submission failed. Please try again."); }
      setState("sent");
    } catch (e2) { setState("error"); setErr(e2.message || "Something went wrong. Please email info@dynacert.com."); }
  };
  return (<>
    <PageHead crumb="Home / Contact" eyebrow="Discuss Your Application" title={contact.title} lead={contact.intro} />
    <section className="section paper2"><div className="container"><div className="grid g2" style={{ gap: 48, alignItems: "start" }}>
      {state === "sent" ? (
        <div className="card" data-testid="form-success" style={{ padding: 32 }}><BadgeCheck size={30} color="var(--brand-2)" />
          <div className="h3" style={{ marginTop: 14 }}>Thank you — your enquiry has been received.</div>
          <p style={{ color: "#3f4f5c", marginTop: 10 }}>Our team will review your operating profile and respond. For anything urgent you can also email <a href="mailto:info@dynacert.com" style={{ color: "var(--brand)", fontWeight: 700 }}>info@dynacert.com</a>.</p></div>
      ) : (
      <form data-testid="assessment-form" onSubmit={onSubmit}>
        <div className="mega-col-title">Step 1 — required</div>
        <div className="grid g2" style={{ gap: 16 }}>
          <div><label className="lbl">Name*</label><input name="name" className="fld" required data-testid="f-name" /></div>
          <div><label className="lbl">Company*</label><input name="company" className="fld" required data-testid="f-company" /></div>
          <div><label className="lbl">Email*</label><input name="email" className="fld" type="email" required data-testid="f-email" /></div>
          <div><label className="lbl">Country*</label><input name="country" className="fld" required data-testid="f-country" /></div></div>
        <div style={{ marginTop: 16 }}><label className="lbl">Industry / application*</label><input name="industry" className="fld" required data-testid="f-industry" placeholder="e.g. Ports — container crane" /></div>
        <div style={{ marginTop: 16 }}><label className="lbl">Primary objective*</label>
          <select name="objective" className="fld" required data-testid="f-objective"><option value="">Select…</option>
            <option>Reduce fuel cost</option><option>Reduce emissions</option><option>ESG / reporting</option><option>Improve uptime / TCO</option><option>Explore carbon value</option><option>Other</option></select></div>
        <div className="mega-col-title" style={{ marginTop: 26 }}>Step 2 — optional (technical, if known)</div>
        <div className="grid g2" style={{ gap: 16 }}>
          <div><label className="lbl">Engine manufacturer / model</label><input name="engine" className="fld" data-testid="f-engine" /></div>
          <div><label className="lbl">Number of units</label><input name="units" className="fld" data-testid="f-units" /></div></div>
        <label className="check" style={{ marginTop: 20, fontSize: ".9rem" }}><input type="checkbox" name="consent" required data-testid="f-consent" /> <span>I agree to dynaCERT processing this enquiry (privacy).</span></label>
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "auto", width: 1, height: 1, overflow: "hidden" }}><label>Leave this field empty<input type="text" name="website" tabIndex={-1} autoComplete="off" /></label></div>
        <div style={{ marginTop: 22 }}><button className="btn btn-primary" type="submit" disabled={state === "loading"} data-testid="submit-assessment">{state === "loading" ? "Sending…" : "Request an Application Assessment"}<ArrowRight size={16} /></button></div>
        {state === "error" && <p data-testid="form-error" style={{ marginTop: 16, color: "#b3261e", fontWeight: 700 }}>{err}</p>}
      </form>
      )}
      <div>{contact.routes.map((r) => <div key={r.scope} className="card" style={{ marginBottom: 16 }}><div className="tag">{r.scope}</div>
        <div className="h3" style={{ marginTop: 12, fontSize: "1.12rem" }}>{r.name}</div><div className="small">{r.role}</div>
        <a href={`mailto:${r.email}`} style={{ color: "var(--brand)", fontWeight: 700, fontSize: ".9rem" }}>{r.email}</a></div>)}
        <div className="small" style={{ color: "var(--steel)" }}>{contact.offices.join(" · ")}</div></div>
    </div></div></section>
  </>);
}

/* ---------------- Hero A/B comparison (review-only; does NOT alter the live homepage hero) ---------------- */
function HeroVariant({ unit, unitAlt, testid }) {
  const h = home;
  return (
    <section className="hero-full layered" data-testid={testid}>
      <img src={h.hero.image} alt={h.hero.alt} className="hero-bg" />
      <div className="hero-scrim" />
      <div className="hero-glow" aria-hidden="true" />
      <img src={unit} alt={unitAlt} className="hero-unit" data-testid={`${testid}-unit`} />
      <div className="container hero-inner">
        <div className="eyebrow on-dark rise">{h.hero.eyebrow}</div>
        <h1 className="h1 rise rise-2" style={{ marginTop: 18, maxWidth: "15ch" }}>{h.hero.title}</h1>
        <p className="lead rise rise-3" style={{ color: "#cdd8e2", marginTop: 22, maxWidth: "50ch" }}>{h.hero.sub}</p>
        <div className="rise rise-4" style={{ display: "flex", gap: 14, marginTop: 30, flexWrap: "wrap" }}>
          <Btn href={h.hero.primary_cta.href} testid={`${testid}-cta`}>{h.hero.primary_cta.label}</Btn>
          <Btn href={h.hero.secondary_cta.href} variant="ghost" dark testid={`${testid}-secondary`}>{h.hero.secondary_cta.label}</Btn></div>
        <div className="hero-markers rise rise-4">{h.hero.markers.map((m) => <span key={m} className="hero-pill">{m}</span>)}</div>
      </div>
    </section>
  );
}
const HeroCompare = () => (
  <div data-testid="hero-compare">
    <div className="ab-bar"><div className="container ab-bar-in">
      <span className="ab-title">Hero A/B — product & art-direction comparison</span>
      <div className="ab-links"><a href="/hero-a" data-testid="ab-view-a">Variant A full ↗</a><a href="/hero-b" data-testid="ab-view-b">Variant B full ↗</a><a href="/">Back to site</a></div>
    </div></div>
    <div className="ab-label"><span className="ab-tag a">Variant A · HG6C (current)</span></div>
    <HeroVariant unit={home.hero.unit} unitAlt={home.hero.unitAlt} testid="hero-variant-a" />
    <div className="ab-label"><span className="ab-tag b">Variant B · HG1R white cabinet</span></div>
    <HeroVariant unit={home.hero.unitWhite} unitAlt={home.hero.unitWhiteAlt} testid="hero-variant-b" />
  </div>
);
const HeroSingle = ({ variant }) => {
  const isA = variant === "a";
  return (
    <div data-testid={`hero-single-${variant}`}>
      <div className="ab-label float"><span className={`ab-tag ${variant}`}>{isA ? "Variant A · HG6C (current)" : "Variant B · HG1R white cabinet"}</span>
        <a href="/hero-compare" className="ab-mini">A/B compare</a><a href={isA ? "/hero-b" : "/hero-a"} className="ab-mini">{isA ? "View B" : "View A"}</a></div>
      <HeroVariant unit={isA ? home.hero.unit : home.hero.unitWhite} unitAlt={isA ? home.hero.unitAlt : home.hero.unitWhiteAlt} testid={`hero-full-${variant}`} />
    </div>
  );
};

/* ---------------- Legacy archive (transitional safety net) ---------------- */
function LegacyArchive() {
  const groups = [
    { t: "Investor Relations (legacy)", links: [
      ["Investor Centre", "https://dynacert.com/page/investor-centre"],
      ["Financial Statements · MD&A · Disclosure documents", "https://dynacert.com/page/investor-centre"],
      ["Constating documents · Policies / Governance", "https://dynacert.com/page/investor-centre"],
    ] },
    { t: "News & Press Releases (legacy)", links: [
      ["News — all releases", "https://news.dynacert.com/"],
      ["Press releases (English)", "https://news.dynacert.com/category/press-releases-english-4994"],
    ] },
    { t: "Retained legacy resources (no new equivalent yet)", links: [
      ["FAQ (legacy)", "https://dynacert.com/page/faq"],
      ["Media (legacy)", "https://dynacert.com/page/media"],
      ["Privacy policy (legacy)", "https://dynacert.com/page/privacy-policy"],
    ] },
  ];
  return (<>
    <PageHead crumb="Home / Legacy archive" eyebrow="Transitional archive" title="Legacy content & archive" lead="During the website transition these existing dynaCERT resources remain available while new sections are completed. Regulatory documents and historical news are served by dynaCERT's existing systems." tight />
    <section className="section paper2"><div className="container">
      {groups.map((g) => <div key={g.t} style={{ marginBottom: 34 }}>
        <div className="mega-col-title">{g.t}</div>
        <div className="grid g3" style={{ marginTop: 14 }}>
          {g.links.map(([label, href]) => <a key={label + href} href={href} target="_blank" rel="noreferrer" className="card" data-testid="legacy-link"><ExternalLink size={18} color="var(--brand)" /><div className="h3" style={{ marginTop: 10, fontSize: "1.05rem" }}>{label}</div><span className="small" style={{ wordBreak: "break-word", color: "var(--steel)" }}>{href}</span></a>)}
        </div>
      </div>)}
      <p className="notice">These links point to dynaCERT's current (legacy) systems and open in a new tab. They are preserved so historical URLs and documents keep working during the staged migration.</p>
    </div></section>
    <CtaBand />
  </>);
}

/* ---------------- Router ---------------- */
function Router() {
  const p = usePath();
  const [, path, sub] = p.split("/");
  useEffect(() => { applySeo(p); }, [p]);
  if (!path) return <Home />;
  if (path === "technology") return <Technology />;
  if (path === "products") return sub ? <ProductDetail slug={sub} /> : <ProductsIndex />;
  if (path === "applications") return sub ? <ApplicationDetail slug={sub} /> : <ApplicationsIndex />;
  if (path === "industries") return <IndustryDetail slug={sub} />;
  if (path === "why-hydragen") return <WhyHydraGEN />;
  if (path === "resources") return <Resources />;
  if (path === "company") { if (sub === "leadership") return <Leadership />; if (sub === "global-presence") return <GlobalPresence />; if (sub === "careers") return <Careers />; if (sub === "about") return <About />; return <Company />; }
  if (path === "investors") return sub ? <IRSection slug={sub} /> : <Investors />;
  if (path === "value") return <ValuePage slug={sub} />;
  if (path === "hydralytica") return <HydraLytica />;
  if (path === "legal") return <LegalPage slug={sub} />;
  if (path === "contact") return <Contact />;
  return <NotFound />;
}
export default function App() {
  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || a.target === "_blank" || a.hasAttribute("download")) return;
      if (/^[a-z]+:/i.test(href) || href.startsWith("//") || href.startsWith("#")) return;
      if (!href.startsWith("/")) return;
      e.preventDefault();
      navigate(href);
    };
    const onHash = () => { if (window.location.hash === "#principle") document.getElementById("principle")?.scrollIntoView({ behavior: "smooth" }); };
    document.addEventListener("click", onClick);
    window.addEventListener("hashchange", onHash);
    return () => { document.removeEventListener("click", onClick); window.removeEventListener("hashchange", onHash); };
  }, []);
  return <div className="App"><Header /><main><Router /></main><Footer /></div>;
}
